/* ----------------------------- small helpers ----------------------------- */

function loadSparkTranslations(productId) {
  const defaults = {
    local_label: "Lager Rjukan",
    remote_label: "Lager Danmark",
    none_label: "Ikke på lager",
    stk_suffix: "stk",
  };

  const el = document.getElementById(`SparkInventoryTranslations-${productId}`);
  if (!el) return defaults;

  try {
    const parsed = JSON.parse(el.textContent || "{}");
    return {
      local_label: parsed.local_label || defaults.local_label,
      remote_label: parsed.remote_label || defaults.remote_label,
      none_label: parsed.none_label || defaults.none_label,
      stk_suffix: parsed.stk_suffix || defaults.stk_suffix,
    };
  } catch (e) {
    console.error("SparkInventoryTranslations JSON parse error", e);
    return defaults;
  }
}

function isRetailPriceModeOn() {
  return document.documentElement.getAttribute("data-b2b-show-retail-price") === "1";
}

function getB2BPriceRoot(productId) {
  return document.querySelector(`.b2b-price[data-b2b-price-root="true"][data-product-id="${productId}"]`);
}

function loadB2BDiscountPercent(productId) {
  const root = getB2BPriceRoot(productId);
  if (!root) return 0;

  const raw = (root.getAttribute("data-b2b-discount-percent") || "0").toString().replace(",", ".");
  const value = parseFloat(raw);

  if (!Number.isFinite(value) || value <= 0) return 0;
  return value;
}

function loadB2BIncludeVat(productId) {
  const root = getB2BPriceRoot(productId);
  if (!root) return false;
  return root.getAttribute("data-b2b-include-vat") === "true";
}

function loadB2BVatRate(productId) {
  const root = getB2BPriceRoot(productId);
  if (!root) return 25;

  const v = parseFloat(root.getAttribute("data-b2b-vat-rate") || "25");
  return Number.isFinite(v) ? v : 25;
}

function sparkNorm(s) {
  return (s || "").toString().trim();
}

function sparkNormSpace(s) {
  return sparkNorm(s).replace(/\s+/g, " ");
}

function sparkIsFiniteNum(x) {
  return Number.isFinite(x);
}

/* --------------------------- size order map load -------------------------- */

const SPARK_SIZE_MAP_URL = window.__SIZE_ORDER_MAP_URL || "/assets/size-order-map.json";

let __sparkSizeMapPromise = null;
let __sparkSizeMap = null;

function loadSparkSizeMapOnce() {
  if (__sparkSizeMapPromise) return __sparkSizeMapPromise;

  __sparkSizeMapPromise = fetch(SPARK_SIZE_MAP_URL, { credentials: "same-origin" })
    .then((r) => (r.ok ? r.json() : null))
    .then((map) => {
      __sparkSizeMap = map || null;
      return __sparkSizeMap;
    })
    .catch(() => {
      __sparkSizeMap = null;
      return null;
    });

  return __sparkSizeMapPromise;
}

function sparkGetRank(map, raw) {
  if (!map) return null;

  const s0 = sparkNorm(raw);
  if (!s0) return null;

  if (map[s0] != null) return map[s0];

  const s1 = sparkNormSpace(s0);
  if (map[s1] != null) return map[s1];

  const dot = s0.replace(",", ".");
  if (map[dot] != null) return map[dot];

  const comma = s0.replace(".", ",");
  if (map[comma] != null) return map[comma];

  const dot2 = s1.replace(",", ".");
  if (map[dot2] != null) return map[dot2];

  const comma2 = s1.replace(".", ",");
  if (map[comma2] != null) return map[comma2];

  return null;
}

/* ------------------------- matrix: color header fix ------------------------ */

const SPARK_MATRIX_COLOR_LABEL_NO = window.__sparkMatrixColorLabelNo || "Farge";

function patchSparkMatrixColorHeader(target) {
  const root = target?.shadowRoot ? target.shadowRoot : target;
  if (!root) return false;

  let changed = false;

  root.querySelectorAll("thead tr th:first-child").forEach((th) => {
    const txt = sparkNormSpace(th.textContent);
    if (/^color$/i.test(txt)) {
      th.textContent = SPARK_MATRIX_COLOR_LABEL_NO;
      changed = true;
    }
  });

  return changed;
}

/* ----------------------- matrix: hide -> show styles ----------------------- */

function ensureSparkMatrixSortStyles(matrix) {
  const root = matrix?.shadowRoot;
  if (!root) return;

  if (root.querySelector('style[data-spark-size-sort-style="1"]')) return;

  const style = document.createElement("style");
  style.setAttribute("data-spark-size-sort-style", "1");
  style.textContent = `
    :host([data-spark-size-hide="1"]) table { opacity:0; pointer-events:none; transition:none; }
    :host([data-spark-size-hide="1"][data-spark-size-ready="1"]) table { opacity:1; pointer-events:auto; transition: opacity 220ms ease; }
    :host([data-spark-size-hide="1"][data-spark-size-ready="1"][data-spark-size-noanim="1"]) table { transition:none; }
  `;
  root.appendChild(style);
}

/* ------------------------- matrix: sticky header (modal scroll) ------------------------- */

function setupSparkMatrixModalStickyHeader(matrix) {
  const root = matrix?.shadowRoot;
  if (!root) return;

  const wrap = root.querySelector("div.overflow-x-auto");
  if (!wrap) return;

  wrap.style.overflowX = "auto";
  wrap.style.overflowY = "auto";

  wrap.style.maxHeight = "calc(100vh - 220px)";

  let style = root.querySelector('style[data-spark-native-sticky="1"]');
  if (!style) {
    style = document.createElement("style");
    style.setAttribute("data-spark-native-sticky", "1");
    style.textContent = `
      thead th{
        position: sticky !important;
        top: 0 !important;
        z-index: 1 !important;
        background: var(--spark-matrix-sticky-bg, #fff) !important;
        box-shadow: 0 1px 0 rgba(0,0,0,0.08);
      }
    `;
    root.appendChild(style);
  } else {
    root.appendChild(style);
  }
}

/* -------------------------- matrix: sorting logic -------------------------- */

const __sparkSortState = new WeakMap();

function getSparkSortState(matrix) {
  let s = __sparkSortState.get(matrix);
  if (!s) {
    s = { applying: false, obs: null };
    __sparkSortState.set(matrix, s);
  }
  return s;
}

function sortSparkMatrixColumnsIfNeeded(matrix, map) {
  if (!matrix || !matrix.shadowRoot) return false;
  if (!map) return false;

  const root = matrix.shadowRoot;
  const table = root.querySelector("table");
  const headerRow = root.querySelector("thead tr");
  const bodyRows = root.querySelectorAll("tbody tr");

  if (!table || !headerRow || !bodyRows.length) return false;

  const headerCells = Array.from(headerRow.children);
  if (headerCells.length < 2) return false;

  const sizeCells = headerCells.slice(1);

  const infos = sizeCells
    .map((cell, idx) => {
      const label = sparkNormSpace(cell.textContent);
      return { cell, idx, label, key: label.toLowerCase() };
    })
    .filter((x) => x.key);

  if (!infos.length) return false;

  const currentSig = infos.map((x) => x.key).join("|");

  const sortedInfos = [...infos].sort((a, b) => {
    const ra = sparkGetRank(map, a.label);
    const rb = sparkGetRank(map, b.label);

    const A = sparkIsFiniteNum(ra) ? ra : 1e9;
    const B = sparkIsFiniteNum(rb) ? rb : 1e9;

    if (A !== B) return A - B;
    return a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: "base" });
  });

  const sortedSig = sortedInfos.map((x) => x.key).join("|");
  if (currentSig === sortedSig) return false;

  ensureSparkMatrixSortStyles(matrix);

  const st = getSparkSortState(matrix);
  st.applying = true;

  // hide while reordering columns
  matrix.setAttribute("data-spark-size-hide", "1");
  matrix.removeAttribute("data-spark-size-ready");
  void matrix.offsetWidth;

  for (const info of sortedInfos) headerRow.appendChild(info.cell);

  bodyRows.forEach((row) => {
    const cells = Array.from(row.children);
    if (cells.length < 2) return;

    const tds = cells.slice(1);
    if (!tds.length) return;

    const frag = document.createDocumentFragment();
    const used = new Set();

    for (const info of sortedInfos) {
      const td = tds[info.idx];
      if (td) {
        used.add(info.idx);
        frag.appendChild(td);
      }
    }

    for (let i = 0; i < tds.length; i++) {
      if (!used.has(i)) frag.appendChild(tds[i]);
    }

    row.appendChild(frag);
  });

  requestAnimationFrame(() => {
    st.applying = false;
    if (!matrix.isConnected) return;
    matrix.setAttribute("data-spark-size-ready", "1");
  });

  return true;
}

function attachSparkMatrixSizeSorter(productId) {
  const matrix = document.querySelector(`spark-product-matrix[parent-id="${productId}"]`);
  if (!matrix || !matrix.shadowRoot) return;

  const st = getSparkSortState(matrix);
  if (st.obs) return;

  ensureSparkMatrixSortStyles(matrix);

  const run = () => {
    if (st.applying) return;
    sortSparkMatrixColumnsIfNeeded(matrix, __sparkSizeMap);
  };

  loadSparkSizeMapOnce().then(() => {
    run();
    queueMicrotask(run);
  });

  st.obs = new MutationObserver(() => queueMicrotask(run));
  st.obs.observe(matrix.shadowRoot, { childList: true, subtree: true });
}

/* ---------------------- image helper (needed on PDP) ---------------------- */

function getMainProductImageUrl() {
  const img =
    document.querySelector("media-gallery img[src]") ||
    document.querySelector(".product__media img[src]") ||
    document.querySelector("img.product__media-item-image[src]") ||
    document.querySelector('img[src*="cdn.shopify.com"][src*="/files/"]');

  const src = img?.getAttribute("src") || "";
  if (!src) return null;

  try {
    const u = new URL(src, location.origin);
    u.search = "";
    return u.toString();
  } catch {
    return src.split("?")[0];
  }
}

/* ---------------------------- matrix decoration ---------------------------- */

function applyB2BAdjustmentsToPriceEl(priceEl, discountPercent, includeVat, vatRate) {
  if (!priceEl) return;

  if (priceEl.dataset.sparkB2bAdjusted === "true") return;

  const originalText = priceEl.textContent || "";
  const normalized = originalText.replace(/\u00A0/g, " ");

  const match = normalized.match(/([0-9]+(?:[.,][0-9]+)?)/);
  if (!match) return;

  const numberStr = match[1];
  const base = parseFloat(numberStr.replace(",", "."));
  if (!Number.isFinite(base) || base <= 0) return;

  let finalNet = base;
  if (discountPercent && discountPercent > 0.0001) {
    finalNet = base * (1 - discountPercent / 100);
  }

  let display = finalNet;
  if (includeVat) display = finalNet * (1 + (vatRate || 25) / 100);

  display = Math.round((display + Number.EPSILON) * 100) / 100;

  let displayStr = display.toFixed(2);
  if (numberStr.indexOf(",") >= 0) displayStr = displayStr.replace(".", ",");

  const updated = normalized.replace(numberStr, displayStr);
  priceEl.textContent = updated;
  priceEl.dataset.sparkB2bAdjusted = "true";
}

function setupSparkMatrixDecorator(productId, inventoryBySku, mediaMeta, t, discountPercent, includeVat, vatRate, onFirstDecorated) {
  let firstDone = false;

  function applySparkThumbRule(matrix) {
    const root = matrix?.shadowRoot;
    if (!root) return;

    const rows = root.querySelectorAll("tbody tr");
    if (!rows.length) return;

    const multiColor = !!mediaMeta?.multiColor;

    rows.forEach((row) => {
      const th = row.querySelector("th");
      const img = th?.querySelector("img.sl-pdp-variant-image-thumbnail");
      if (!img) return;

      if (!multiColor) {
        img.style.removeProperty("display");
        return;
      }

      const skuSpan = row.querySelector("span.text-sm.leading-tight.text-default-body");
      const m = skuSpan?.textContent?.match(/SKU:\s*(\S+)/i);
      const sku = m?.[1]?.trim();

      if (!sku) {
        img.style.removeProperty("display");
        return;
      }

      const hasMedia = mediaMeta?.hasMediaBySku?.[sku] === true;
      if (!hasMedia) img.style.setProperty("display", "none", "important");
      else img.style.removeProperty("display");
    });
  }

  function decorateSparkMatrix() {
    const matrix = document.querySelector(`spark-product-matrix[parent-id="${productId}"]`);
    if (!matrix || !matrix.shadowRoot) return false;

    const root = matrix.shadowRoot;

    // needed for modal: sticky header in vertical scroll
    setupSparkMatrixModalStickyHeader(matrix);

    patchSparkMatrixColorHeader(root);

    const rows = root.querySelectorAll("tbody tr");
    if (!rows.length) return false;

    applySparkThumbRule(matrix);

    // "not available" cell cleanup
    const unavailableBlocks = root.querySelectorAll("td .text-error");
    unavailableBlocks.forEach((block) => {
      const flex = block.querySelector(".flex.items-center.gap-4.text-center");
      if (!flex) return;

      const icon = flex.querySelector("svg-circle-error");
      if (icon) icon.remove();

      const p = flex.querySelector("p");
      if (p) {
        p.style.fontSize = "1.35rem";
        p.style.lineHeight = "1rem";
        p.style.color = "#707070";
      }
    });

    rows.forEach((row) => {
      row.querySelectorAll("td").forEach((td) => {
        const skuSpan = td.querySelector("span.text-sm.leading-tight.text-default-body");
        if (!skuSpan) return;

        const match = skuSpan.textContent.match(/SKU:\s*(\S+)/i);
        if (!match) return;

        const sku = match[1].trim();
        const variant = inventoryBySku.get(sku);
        if (!variant) return;

        const local = Number(variant.rjukanlager || 0);
        const remote = Number(variant.fjernlager || 0);

        const statusWrapper = td.querySelector(".group.flex.items-center");
        const dot = statusWrapper?.querySelector(".size-3");
        const inventoryTooltip = dot?.querySelector("div.absolute");

        if (statusWrapper && dot && inventoryTooltip) {
          let textClass = "";
          let bgClass = "";
          let borderClass = "";
          let tooltipText = "";

          if (local > 0) {
            textClass = "text-success";
            bgClass = "bg-success";
            borderClass = "after:border-t-success";
            tooltipText = t.local_label;
          } else if (remote > 0) {
            textClass = "text-warn";
            bgClass = "bg-warn";
            borderClass = "after:border-t-warn";
            tooltipText = t.remote_label;
          } else {
            textClass = "text-error";
            bgClass = "bg-error";
            borderClass = "after:border-t-error";
            tooltipText = t.none_label;
          }

          statusWrapper.classList.remove("text-success", "text-warn", "text-error", "text-info");
          dot.classList.remove("bg-success", "bg-warn", "bg-error", "bg-info");
          inventoryTooltip.classList.remove("bg-success", "bg-warn", "bg-error", "bg-info");

          for (const cls of [...inventoryTooltip.classList]) {
            if (cls.startsWith("after:border-t-")) inventoryTooltip.classList.remove(cls);
          }

          statusWrapper.classList.add(textClass);
          dot.classList.add(bgClass);
          inventoryTooltip.classList.add(bgClass, borderClass);
          if (inventoryTooltip.textContent !== tooltipText) {
            inventoryTooltip.textContent = tooltipText;
          }
        }

        // add per-warehouse stock lines (once per tooltip)
        const tooltipSku = td.querySelector(".absolute.z-10.hidden.w-max.flex-col");
        const list = tooltipSku?.querySelector("ul");

        if (list && !list.querySelector("[data-stock-info-added]")) {
          const addRow = (text, mark = false) => {
            const li = document.createElement("li");
            li.className = "flex gap-x-1.5";
            if (mark) li.setAttribute("data-stock-info-added", "true");

            const skuIcon = list.querySelector("li svg-info");
            if (skuIcon) li.appendChild(skuIcon.cloneNode(true));

            const span = document.createElement("span");
            span.className = "text-sm leading-tight text-default-body";
            span.textContent = text;

            li.appendChild(span);
            list.appendChild(li);
          };

          let first = true;

          if (local > 0) {
            addRow(`${t.local_label}: ${local} ${t.stk_suffix}`, first);
            first = false;
          }

          if (remote > 0) {
            addRow(`${t.remote_label}: ${remote} ${t.stk_suffix}`, first);
            first = false;
          }

          if (local <= 0 && remote <= 0) {
            addRow(t.none_label, first);
          }
        }

        // price patch (optional)
        const priceEl = td.querySelector("p.price");
        if (priceEl) {
          if (isRetailPriceModeOn()) {
            priceEl.style.setProperty("display", "none", "important");
          } else {
            priceEl.style.removeProperty("display");
            applyB2BAdjustmentsToPriceEl(priceEl, discountPercent, includeVat, vatRate);
          }
        }
      });
    });

    if (!firstDone) {
      firstDone = true;
      if (typeof onFirstDecorated === "function") onFirstDecorated();
    }

    bindSparkPortalTooltips(matrix);

    return true;
  }

  // Re-apply when retail price toggle changes
  if (!window.__b2bRetailToggleObserverAttached) {
    window.__b2bRetailToggleObserverAttached = true;

    const mo = new MutationObserver(() => {
      decorateSparkMatrix();
    });

    mo.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-b2b-show-retail-price"],
    });
  }

  // Initial polling until matrix first appears + has rows
  let attempts = 0;
  const maxAttempts = 80;

  const timer = setInterval(() => {
    const done = decorateSparkMatrix();
    if (done || attempts++ > maxAttempts) clearInterval(timer);
  }, 120);

  // Also: observe matrix subtree once it exists, to re-decorate on Spark rerenders
  const attachObserverWhenReady = () => {
    const matrix = document.querySelector(`spark-product-matrix[parent-id="${productId}"]`);
    if (!matrix?.shadowRoot) return false;

    if (matrix.shadowRoot.__sparkDecorObserverAttached) return true;
    matrix.shadowRoot.__sparkDecorObserverAttached = true;

    let raf = 0;
    const schedule = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        decorateSparkMatrix();
      });
    };

    const mo = new MutationObserver(schedule);
    mo.observe(matrix.shadowRoot, { childList: true, subtree: true });

    return true;
  };

  // try attach observer shortly after
  let oAttempts = 0;
  const oTimer = setInterval(() => {
    if (attachObserverWhenReady() || oAttempts++ > 120) clearInterval(oTimer);
  }, 150);
}

/* ---------------------------- init orchestration --------------------------- */

window.__sparkMatrixInit = window.__sparkMatrixInit || {};
const __sparkInitedMatrices = new WeakSet();

function initSparkMatrixForProduct(productId) {
  const cfg = window.__sparkMatrixInit?.[productId];
  if (!cfg) return;

  let attempts = 0;
  const maxAttempts = 160;

  const tick = () => {
    const matrix = document.querySelector(`spark-product-matrix[parent-id="${productId}"]`);
    if (!matrix || !matrix.shadowRoot) {
      if (attempts++ < maxAttempts) return;
      clearInterval(timer);
      return;
    }

    if (__sparkInitedMatrices.has(matrix)) {
      clearInterval(timer);
      return;
    }
    __sparkInitedMatrices.add(matrix);

    // prevent flicker
    matrix.style.opacity = "0";
    matrix.style.transition = "opacity 180ms ease";

    let decorReady = false;
    let sortReady = false;

    const revealIfReady = () => {
      if (!matrix.isConnected) return;
      if (!decorReady || !sortReady) return;
      matrix.style.opacity = "1";
    };

    setupSparkMatrixDecorator(
      productId,
      cfg.inventoryBySku,
      cfg.mediaMeta,
      cfg.translations,
      cfg.b2bDiscountPercent,
      cfg.includeVat,
      cfg.vatRate,
      () => {
        decorReady = true;
        revealIfReady();
      }
    );

    loadSparkSizeMapOnce().then(() => {
      // first sort pass BEFORE showing
      try {
        ensureSparkMatrixSortStyles(matrix);
        sortSparkMatrixColumnsIfNeeded(matrix, __sparkSizeMap);
        // even if no changes were required, mark "ready" after a frame
        requestAnimationFrame(() => {
          sortReady = true;
          revealIfReady();
        });
      } catch {
        sortReady = true;
        revealIfReady();
      }

      // keep sorting on later Spark rerenders
      attachSparkMatrixSizeSorter(productId);
    });

    clearInterval(timer);
  };

  const timer = setInterval(tick, 80);
  tick();
}

/* ----------------------- tooltip portal (no clipping) ---------------------- */

const __sparkTooltip = (() => {
  let el = null;
  let visible = false;
  let lastRect = null;
  let lastLines = [];
  let raf = 0;

  function ensure() {
    if (el && el.isConnected) return el;

    el = document.createElement("div");
    el.id = "SparkMatrixTooltipPortal";
    el.style.position = "fixed";
    el.style.zIndex = "999999";
    el.style.maxWidth = "320px";
    el.style.pointerEvents = "none";
    el.style.display = "none";
    el.style.padding = "8px 10px";
    el.style.borderRadius = "8px";
    el.style.boxShadow = "0 10px 30px rgba(0,0,0,0.18)";
    el.style.border = "1px solid rgba(0,0,0,0.12)";
    el.style.background = "#fff";
    el.style.color = "#111";
    el.style.fontSize = "13px";
    el.style.lineHeight = "1.25";
    el.style.whiteSpace = "normal";

    document.body.appendChild(el);
    return el;
  }

  function setContent(lines) {
    const node = ensure();
    node.textContent = "";
    const ul = document.createElement("ul");
    ul.style.margin = "0";
    ul.style.padding = "0";
    ul.style.listStyle = "none";

    for (const ln of lines) {
      const li = document.createElement("li");
      li.textContent = ln;
      li.style.margin = "0";
      li.style.padding = "0";
      ul.appendChild(li);
    }
    node.appendChild(ul);
  }

  function clamp(v, min, max) {
    return Math.max(min, Math.min(max, v));
  }

  function position(rect) {
    const node = ensure();
    if (!rect) return;

    // make visible to measure
    node.style.display = "block";
    node.style.opacity = "1";

    const pad = 10;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // measure after display
    const w = node.offsetWidth;
    const h = node.offsetHeight;

    // prefer top, else bottom
    const spaceTop = rect.top;
    const spaceBottom = vh - rect.bottom;

    let top;
    if (spaceTop >= h + 12) {
      top = rect.top - h - 8;
    } else if (spaceBottom >= h + 12) {
      top = rect.bottom + 8;
    } else {
      // fallback: clamp inside viewport
      top = clamp(rect.top - h - 8, pad, vh - h - pad);
    }

    // horizontal: center, but flip to fit viewport
    let left = rect.left + rect.width / 2 - w / 2;
    left = clamp(left, pad, vw - w - pad);

    node.style.left = `${Math.round(left)}px`;
    node.style.top = `${Math.round(top)}px`;
  }

  function show(lines, rect) {
    visible = true;
    lastRect = rect;
    lastLines = lines;
    setContent(lines);
    position(rect);
  }

  function hide() {
    visible = false;
    lastRect = null;
    lastLines = [];
    if (el) el.style.display = "none";
  }

  function scheduleReposition() {
    if (!visible || !lastRect) return;
    if (raf) return;
    raf = requestAnimationFrame(() => {
      raf = 0;
      // recompute from DOM if possible: rect is snapshot, but good enough.
      position(lastRect);
    });
  }

  // keep tooltip aligned during scroll/resize (capture scroll from any container)
  window.addEventListener("scroll", scheduleReposition, true);
  window.addEventListener("resize", scheduleReposition);

  return { show, hide };
})();

function bindSparkPortalTooltips(matrix) {
  const root = matrix?.shadowRoot;
  if (!root) return;

  root.querySelectorAll(".group.flex.items-center .size-3").forEach((dot) => {
    if (dot.dataset.sparkPortalBound === "1") return;
    dot.dataset.sparkPortalBound = "1";

    const nativeTip = dot.querySelector("div.absolute");

    if (nativeTip) {
      nativeTip.style.setProperty("display", "none", "important");
      nativeTip.style.setProperty("opacity", "0", "important");
      nativeTip.style.setProperty("visibility", "hidden", "important");
      nativeTip.style.setProperty("pointer-events", "none", "important");
    }

    dot.addEventListener("pointerenter", () => {
      const txt = (nativeTip?.textContent || "").trim();
      if (!txt) return;
      const r = dot.getBoundingClientRect();
      __sparkTooltip.show([txt], r);
    });

    dot.addEventListener("pointerleave", () => __sparkTooltip.hide());
  });

  root.querySelectorAll("div.group.mx-1").forEach((group) => {
    const box = group.querySelector(".absolute.z-10, .absolute.z-20, .absolute.z-50");
    const ul = box?.querySelector("ul");
    if (!ul) return;

    if (group.dataset.sparkPortalBound === "1") return;
    group.dataset.sparkPortalBound = "1";

    box.style.display = "none";

    group.addEventListener("pointerenter", () => {
      const lines = Array.from(ul.querySelectorAll("li span"))
        .map((s) => (s.textContent || "").trim())
        .filter(Boolean);

      if (!lines.length) return;
      const anchor = group.querySelector("svg-info, svg") || group;
      const r = anchor.getBoundingClientRect();
      __sparkTooltip.show(lines, r);
    });

    group.addEventListener("pointerleave", () => __sparkTooltip.hide());
  });
}

/* ------------------------------ boot on PDP ------------------------------ */

document.addEventListener("DOMContentLoaded", () => {
  const productInfo = document.querySelector("product-info[data-product-id]");
  if (!productInfo) return;

  const productId = productInfo.dataset.productId;
  if (!productId) return;

  const mediaMetaEl = document.getElementById(`SparkVariantMediaMeta-${productId}`);
  let mediaMeta = { multiColor: false, hasMediaBySku: {} };

  try {
    mediaMeta = mediaMetaEl ? JSON.parse(mediaMetaEl.textContent || "{}") : mediaMeta;
    if (!mediaMeta || typeof mediaMeta !== "object") mediaMeta = { multiColor: false, hasMediaBySku: {} };
    if (!mediaMeta.hasMediaBySku || typeof mediaMeta.hasMediaBySku !== "object") mediaMeta.hasMediaBySku = {};
  } catch {
    mediaMeta = { multiColor: false, hasMediaBySku: {} };
  }

  const inventoryScript = document.getElementById(`VariantInventory-${productId}`);
  if (!inventoryScript) return;

  let inventory = [];
  try {
    inventory = JSON.parse(inventoryScript.textContent || "[]");
  } catch (e) {
    console.error("VariantInventory JSON parse error", e);
    return;
  }

  const inventoryBySku = new Map(inventory.filter((v) => v.sku).map((v) => [String(v.sku).trim(), v]));

  const translations = loadSparkTranslations(productId);
  const b2bDiscountPercent = loadB2BDiscountPercent(productId);
  const includeVat = loadB2BIncludeVat(productId);
  const vatRate = loadB2BVatRate(productId);

  window.__mainProductImageUrl = window.__mainProductImageUrl || getMainProductImageUrl();

  window.__sparkMatrixInit[productId] = {
    inventoryBySku,
    mediaMeta,
    translations,
    b2bDiscountPercent,
    includeVat,
    vatRate,
  };

  // If matrix is already on page (rare), init immediately
  initSparkMatrixForProduct(productId);
});

document.addEventListener("spark:matrix-mounted", (e) => {
  const productId = e?.detail?.productId;
  if (!productId) return;
  initSparkMatrixForProduct(String(productId));
});
